"use client";

import { c as _c } from "react/compiler-runtime";
import { queryClient } from "@/config/react-query";
import { useWebSocketCallbackSubscription } from "@/hooks/use-websocket-subscription";
import type { AlertDetail } from "@/types/alert";
import type { CallDetail, ChatUpdate } from "@/types/chat";
import { AlertType } from "@/types/enums/alert-type";
import { ChatStatus } from "@/types/enums/chat";
import { UserType } from "@/types/enums/user-type";
import type { MessageSummary, MessageWithChatSummary } from "@/types/message";
import { chatQueryKeys, prependNewChatToInfiniteLists, pruneChatFromInfiniteLists, updateChatOptimistically, updateOrAddMessage } from "@/query/comms/chat";

// Alert types that should trigger chat cache updates
const MESSAGE_ALERT_TYPES = [AlertType.CHAT_UPDATE, AlertType.MESSAGE_DELIVERED, AlertType.MESSAGE_FAILED, AlertType.MESSAGE_SENT, AlertType.MESSAGE_RECEIVED];
const isMessageWithChatSummary = (message: unknown): message is MessageWithChatSummary => {
  return typeof message === "object" && message !== null && "message" in message && "chat" in message;
};

// Type guards for message types
const isMessageSummary = (message: unknown): message is MessageSummary => {
  return typeof message === "object" && message !== null && "messageUid" in message && "content" in message && "sender" in message;
};
const isAssistantSender = (message: unknown): boolean => isMessageSummary(message) && message.sender.type === UserType.ASSISTANT;
const isCallDetail = (message: unknown): message is CallDetail => {
  return typeof message === "object" && message !== null && "direction" in message && "pathToAudio" in message && "summary" in message;
};
const isChatUpdate = (message: unknown): message is ChatUpdate => {
  return typeof message === "object" && message !== null && "id" in message && "updatedFrom" in message && "updatedTo" in message;
};

// Coalesce unread-count refetches. A burst of CHAT_UPDATE status broadcasts (e.g. a
// bulk read/clear, or one event fanned out to many connected clients) would otherwise
// invalidate — and refetch — the unread count once per event. Debouncing collapses the
// burst into a single refetch on the trailing edge.
let unreadCountInvalidateTimer: ReturnType<typeof setTimeout> | null = null;
function scheduleUnreadCountInvalidation() {
  if (unreadCountInvalidateTimer) clearTimeout(unreadCountInvalidateTimer);
  unreadCountInvalidateTimer = setTimeout(() => {
    unreadCountInvalidateTimer = null;
    // Drop the trailing `undefined` (no-location) slot so the key prefix-matches
    // and invalidates the global count plus every location-filtered variant.
    queryClient.invalidateQueries({
      queryKey: chatQueryKeys.unreadCount().filter(part => part !== undefined)
    });
  }, 500);
}

/**
 * Hook that subscribes to chat-related websocket alerts and updates React Query cache.
 * This ensures that all components using React Query for chat data automatically
 * receive real-time updates without needing separate state management.
 */
export function useChatRealtimeCache() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = function handleMessage(alert) {
      const data = alert.data;
      if (isMessageWithChatSummary(data)) {
        const chat = data.chat;
        const message = data.message;
        const isInboundMessage = alert.type === AlertType.MESSAGE_RECEIVED && isMessageSummary(message) && message.sender.type === UserType.PATIENT;
        const isFromAssistant = isAssistantSender(message);
        prependNewChatToInfiniteLists(chat, message, {
          isInboundMessage,
          isFromAssistant
        });
      }
      if (alert.type === AlertType.CHAT_UPDATE && isChatUpdate(data)) {
        const chatUpdate = data;
        const updatedTo = chatUpdate.updatedTo;
        const updatedFrom = chatUpdate.updatedFrom;
        const chatId = updatedTo.id;
        const primaryChanged = updatedFrom.primaryPatientId !== updatedTo.primaryPatientId;
        updateChatOptimistically(chatId, chat_0 => ({
          ...chat_0,
          ...(primaryChanged && {
            primaryPatientId: updatedTo.primaryPatientId,
            patients: updatedTo.patients ?? chat_0.patients
          }),
          status: updatedTo.status ?? chat_0.status,
          patientOptedOutOfSms: updatedTo.patientOptedOutOfSms ?? chat_0.patientOptedOutOfSms,
          clinicOptedOutOfSms: updatedTo.clinicOptedOutOfSms ?? chat_0.clinicOptedOutOfSms
        }), chat_1 => ({
          ...chat_1,
          ...(primaryChanged && {
            primaryPatientId: updatedTo.primaryPatientId,
            patients: updatedTo.patients ?? chat_1.patients
          }),
          status: updatedTo.status ?? chat_1.status,
          latestMessageContent: updatedTo.latestMessageContent ?? chat_1.latestMessageContent,
          latestMessageDate: updatedTo.latestMessageDate ?? chat_1.latestMessageDate,
          patientOptedOutOfSms: updatedTo.patientOptedOutOfSms ?? chat_1.patientOptedOutOfSms,
          clinicOptedOutOfSms: updatedTo.clinicOptedOutOfSms ?? chat_1.clinicOptedOutOfSms
        }));
        if (updatedTo.status === ChatStatus.READ) {
          pruneChatFromInfiniteLists(chatId, _temp);
        }
        if (updatedFrom.status !== updatedTo.status && (updatedTo.status === ChatStatus.READ || updatedFrom.status === ChatStatus.READ)) {
          if (chatUpdate.unreadCounts) {
            queryClient.setQueryData(chatQueryKeys.unreadCount(undefined), chatUpdate.unreadCounts.total);
            for (const [locationId, count] of Object.entries(chatUpdate.unreadCounts.byLocation)) {
              queryClient.setQueryData(chatQueryKeys.unreadCount(locationId), count);
            }
          } else {
            scheduleUnreadCountInvalidation();
          }
        }
      }
      if ([AlertType.MESSAGE_RECEIVED, AlertType.MESSAGE_SENT, AlertType.MESSAGE_DELIVERED, AlertType.MESSAGE_FAILED].includes(alert.type)) {
        if (isMessageWithChatSummary(data)) {
          const {
            message: message_0,
            chat: chat_2
          } = data;
          const chatId_0 = chat_2.id;
          const isFromPatient = message_0.sender.type === UserType.PATIENT;
          const isInboundFromPatient = alert.type === AlertType.MESSAGE_RECEIVED && isFromPatient;
          updateChatOptimistically(chatId_0, chatDetail => {
            const updatedMessages = updateOrAddMessage(chatDetail.messages, message_0);
            return {
              ...chatDetail,
              messages: updatedMessages,
              status: isInboundFromPatient ? chat_2.status : chatDetail.status,
              ...(isFromPatient && {
                patients: chat_2.patients,
                primaryPatientId: chat_2.primaryPatientId,
                mode: chat_2.mode ?? chatDetail.mode
              })
            };
          }, chatSummary => ({
            ...chatSummary,
            latestMessageContent: chat_2.latestMessageContent,
            latestMessageDate: chat_2.latestMessageDate,
            status: chat_2.status,
            patientOptedOutOfSms: chat_2.patientOptedOutOfSms,
            clinicOptedOutOfSms: chat_2.clinicOptedOutOfSms,
            patients: chat_2.patients,
            primaryPatientId: isFromPatient ? chat_2.primaryPatientId : chatSummary.primaryPatientId,
            mode: isFromPatient ? chat_2.mode ?? chatSummary.mode : chatSummary.mode
          }));
        } else {
          if (isMessageSummary(data)) {
            const message_1 = data;
            const chatId_1 = message_1.chatId;
            updateChatOptimistically(chatId_1, chat_3 => {
              const updatedMessages_0 = updateOrAddMessage(chat_3.messages, message_1);
              return {
                ...chat_3,
                messages: updatedMessages_0
              };
            }, chat_4 => ({
              ...chat_4,
              latestMessageContent: message_1.content ?? "",
              latestMessageDate: message_1.createdDate
            }));
          }
        }
        if (isCallDetail(data)) {
          const call = data as CallDetail;
          const chatId_2 = call.chatId;
          updateChatOptimistically(chatId_2, chat_5 => {
            const updatedMessages_1 = updateOrAddMessage(chat_5.messages, call);
            return {
              ...chat_5,
              messages: updatedMessages_1
            };
          }, chat_6 => ({
            ...chat_6,
            latestMessageContent: call.summary,
            latestMessageDate: call.createdDate
          }));
        }
      }
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const handleMessage = t0;
  useWebSocketCallbackSubscription(MESSAGE_ALERT_TYPES, handleMessage, true, "useChatRealtimeCache");
}
function _temp(options) {
  return options.unreadOnly === true;
}